import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, Clock, Loader2, Instagram, Facebook } from "lucide-react";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = `${API}/api/static/hero/contact.jpg`;

const TikTokIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-5.86 11.93 6.84 6.84 0 0 0 11.6-4.93V9.85a8.16 8.16 0 0 0 4.77 1.52V8a4.85 4.85 0 0 1-1.28-.13Z" />
  </svg>
);

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = React.useState(false);

  const handle = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        subject: form.subject || null,
        message: form.message,
      });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Kontak — Konsultasi Umrah & Haji" titleEn="Contact — Umrah & Hajj Consultation" description="Hubungi BATIK Tour & Travel: WA +62 821-1429-4101, batikumrahhaji.bekasi@gmail.com. Konsultasi gratis paket umrah & haji resmi PPIU & PIHK." />
      <section className="relative pt-32 lg:pt-40 pb-12 overflow-hidden isolate">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/40" aria-hidden />
        <div className="pointer-events-none absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-background" aria-hidden />
        <div className="container-x max-w-3xl relative">
          <p className="overline mb-5">{t("nav.contact")}</p>
          <h1 className="h-display text-primary">{t("contact.title")}</h1>
          <p className="mt-6 text-lg text-muted-foreground">{t("contact.sub")}</p>
        </div>
      </section>

      <section className="pb-32">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7 bg-card border border-border rounded-lg p-8 lg:p-12" data-testid="contact-form-card">
            <h2 className="font-serif text-3xl text-primary">{t("contact.formTitle")}</h2>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="c-name" className="text-xs uppercase tracking-wider text-muted-foreground">{t("contact.name")}</Label>
                  <Input id="c-name" data-testid="contact-name" required value={form.name} onChange={handle("name")} className="mt-1.5 bg-background" />
                </div>
                <div>
                  <Label htmlFor="c-email" className="text-xs uppercase tracking-wider text-muted-foreground">{t("contact.email")}</Label>
                  <Input id="c-email" type="email" data-testid="contact-email" required value={form.email} onChange={handle("email")} className="mt-1.5 bg-background" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="c-phone" className="text-xs uppercase tracking-wider text-muted-foreground">{t("contact.phone")}</Label>
                  <Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={handle("phone")} className="mt-1.5 bg-background" />
                </div>
                <div>
                  <Label htmlFor="c-subject" className="text-xs uppercase tracking-wider text-muted-foreground">{t("contact.subject")}</Label>
                  <Input id="c-subject" data-testid="contact-subject" value={form.subject} onChange={handle("subject")} className="mt-1.5 bg-background" />
                </div>
              </div>
              <div>
                <Label htmlFor="c-message" className="text-xs uppercase tracking-wider text-muted-foreground">{t("contact.message")}</Label>
                <Textarea id="c-message" rows={6} data-testid="contact-message" required value={form.message} onChange={handle("message")} className="mt-1.5 bg-background" />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loading ? t("common.sending") : t("common.send")}
              </button>
            </form>
          </div>

          {/* Info */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-primary text-primary-foreground rounded-lg p-8">
              <h3 className="font-serif text-2xl">{t("contact.infoTitle")}</h3>
              <ul className="mt-6 space-y-5 text-sm">
                <li className="flex gap-3">
                  <MapPin className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div>
                    <p className="overline mb-1 !text-accent">{t("contact.address")}</p>
                    <p className="text-white/85">Jaticempaka, Pondok Gede, Bekasi, Jawa Barat</p>
                  </div>
                </li>
                <li className="flex gap-3" data-testid="contact-cs-list">
                  <Phone className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="overline mb-1 !text-accent">Customer Service WhatsApp</p>
                    <ul className="space-y-1">
                      {[
                        { name: "Hasbi",   number: "6282114294101", display: "+62 821-1429-4101" },
                        { name: "Yeyet",   number: "6281932075242", display: "+62 819-3207-5242" },
                        { name: "Hasunah", number: "6289508444277", display: "+62 895-0844-4277" },
                        { name: "Alfi",    number: "62898863793",   display: "+62 898-8637-933"  },
                      ].map((cs) => (
                        <li key={cs.number}>
                          <a
                            href={`https://wa.me/${cs.number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white/85 link-underline"
                            data-testid={`contact-wa-${cs.name.toLowerCase()}`}
                          >
                            {cs.name} — {cs.display}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
                <li className="flex gap-3">
                  <Mail className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div>
                    <p className="overline mb-1 !text-accent">Email</p>
                    <a href="mailto:batikumrahhaji.bekasi@gmail.com" className="text-white/85 link-underline break-all">batikumrahhaji.bekasi@gmail.com</a>
                  </div>
                </li>
                <li className="flex gap-3">
                  <Clock className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div>
                    <p className="overline mb-1 !text-accent">{t("contact.hours")}</p>
                    <p className="text-white/85">{t("contact.hoursVal")}</p>
                  </div>
                </li>
              </ul>

              <div className="mt-8 pt-6 border-t border-white/15">
                <p className="overline mb-3 !text-accent">{t("contact.social")}</p>
                <div className="flex gap-3">
                  <a href="https://instagram.com/batikumrah.bekasi" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                     className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-accent hover:text-primary transition-colors">
                    <Instagram className="h-4 w-4" />
                  </a>
                  <a href="https://tiktok.com/@batikumrah.bekasi" target="_blank" rel="noopener noreferrer" aria-label="TikTok"
                     className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-accent hover:text-primary transition-colors">
                    <TikTokIcon />
                  </a>
                  <a href="https://facebook.com/batikumrah.bekasi" target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                     className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-accent hover:text-primary transition-colors">
                    <Facebook className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden border border-border h-72">
              <iframe
                title="BATIK Tour Office"
                src="https://www.google.com/maps?q=Jaticempaka%20Pondok%20Gede%20Bekasi%20Jawa%20Barat&output=embed"
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
